import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TimesheetsService } from "../services/timesheets";
import { hasTimeAdminAccess } from "../utils/timesUtils";
import {
  setLoading,
  IsLoading,
  loadTimeEntriesInfo,
  SelectNextPersonIndex,
  SelectSearchState,
  SelectTimeEntriesList,
  Summary,
} from "../redux/reducers/approveTimeByUser.reducer";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  Holidays,
  SelectProjectOrWorkOrder,
} from "../redux/reducers/times.reducer";
import { selectMetaTimesData } from "../redux/reducers/metaTimes.reducer";
import {
  hasSuperTimeAdminAccess,
  isSupervisorOrProjectOwner,
  isDeliveryTimeAdminChecker,
} from "../../SharedModule/utils/accessUtils";
import { SelectInitialFilters } from "../redux/reducers/approvalTime.reducer";
import { useTimesMetadata } from "./useTimesMetadata";

export const useApproveTimeByUser = () => {
  useTimesMetadata();
  const dispatch = useDispatch();

  const summary = useSelector(Summary);
  const isLoading = useSelector(IsLoading);
  const timeEntriesList = useSelector(SelectTimeEntriesList);

  const holidays = useSelector(Holidays);
  const selectProjectOrWorkOrder = useSelector(SelectProjectOrWorkOrder);

  const MetaTimesInfo = useSelector(selectMetaTimesData);
  const nextPersonIndex = useSelector(SelectNextPersonIndex);

  const searchState = useSelector(SelectSearchState);
  const initialFilters = useSelector(SelectInitialFilters);

  // times module admin
  const { activeUserId, userRolesList, rolesLoaded } =
    useSelector(selectUserRoles);

  const isTimeAdmin = hasTimeAdminAccess(userRolesList);
  const isSuperTimeAdmin = hasSuperTimeAdminAccess(userRolesList);
  const isSupervisorOrPO = isSupervisorOrProjectOwner(userRolesList);
  const isDeliveryTimeAdmin = isDeliveryTimeAdminChecker(userRolesList);

  const prepareBody = (hoursType: string, user: any) => {
    if (hoursType && user) {
      switch (hoursType) {
        case "billableHours":
          return user.billableIds;
        case "nonBillableHours":
          return user.nonBillableIds;
        case "internalHours":
          return user.internalIds;
        case "workOrderHours":
          return user.workOrderIds;
        case "pendingHours":
          let totalIds = user.billableIds
            .concat(user.nonBillableIds)
            .concat(user.internalIds)
            .concat(user.workOrderIds);
          return totalIds;
        default:
          return [];
      }
    }
    return [];
  };

  const loadPageInfo = useCallback(async () => {
    dispatch(setLoading(true));
    const timeEntriesToApprove =
      await TimesheetsService.getTimeEntriesToApproveByUser(
        prepareBody(searchState.hoursType, searchState.user)
      );
    dispatch(loadTimeEntriesInfo(timeEntriesToApprove.data));
  }, [dispatch, searchState.user]);

  useEffect(() => {
    loadPageInfo();
  }, [loadPageInfo]);

  return {
    isTimeAdmin,
    isSuperTimeAdmin,
    isSupervisorOrPO,
    isDeliveryTimeAdmin,
    summary,
    isLoading,
    rolesLoaded,
    timeEntriesList,
    activeUserId,
    holidays,
    selectProjectOrWorkOrder,
    MetaTimesInfo,
    searchState,
    initialFilters,
    nextPersonIndex,
    loadPageInfo,
  };
};
